import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Component, OnInit, Input, Output, EventEmitter, OnChanges, ViewChild } from '@angular/core';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GroupDescriptor } from '@progress/kendo-data-query';
import { AppConfigService } from '../core/service/app-config.service';
import { GridViewManager } from '../core/model/grid-view-manager';
import { DocumentService } from 'src/app/core/service/document.service';
import { saveAs } from '@progress/kendo-file-saver';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { SendEmailComponent } from '../shared/send-document/send-document.component';
import { DocumentViewerComponent } from '../shared/document-viewer/document-viewer.component';
import { DocumentViewerModel } from '../shared/document-viewer/model/document-viewer';
import { IntlService } from '@progress/kendo-angular-intl';
import { OrganizationService } from '../core/service/organization.service';
import { Member } from '../member/model/member';

@Component({
  selector: 'app-document',
  templateUrl: './document.component.html',
  styleUrls: ['./document.component.scss']
})

export class DocumentComponent implements OnInit, OnChanges
{
  @ViewChild('sendEmail') public sendEmail: SendEmailComponent;
  @ViewChild('documentViewer') public documentViewer: DocumentViewerComponent;

  @Input() public memberId: any;
  @Input() public member: Member;
  @Input() public caseId: any;
  @Input() public taskId: any;
  @Input() public assessmentTaskId: any;
  @Input() public messageId: any;
  @Input() public taskDefinitionId: any;
  @Input() public memberConsentId: any;
  @Input() public bpmId: any;
  @Input() public allowAdd = true;
  @Input() public allowDelete = true;
  @Input() public allowEmail = true;
  @Input() public allowSendCaptureLink = false;
  @Input() public allowSendConsentLink = false;
  @Input() public viewerOnly = false;
  @Input() public loadTasksAttachments = false;  

  @Output() public uploadedDocument: EventEmitter<boolean> = new EventEmitter();
  @Output() public deletedDocument: EventEmitter<boolean> = new EventEmitter();

  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public enableAdvanced = false;
  public count: number;
  public showDocumentCategory = false;
  public showDocumentOrganization = false;
  public organizations = [];
  public consentLinkMode: "image-link" | "consent-request";

  public sendEmailMemberId: number;
  public deleteConfirmationOpened = false;
  public sendLinkDialogOpened = false;
  public documentToDeleteUrl: string;
  public documentToDeleteName: string;
  public deleteForm: any;
  public documentUrl: string;
  public documentName: string;
  public data = [];
  public showTaskColumn = false;

  constructor(
    private appConfigService: AppConfigService,
    private documentService: DocumentService,
    private messageHandlerService: MessageHandlerService,
    private organizationService: OrganizationService,
    public intl: IntlService
  )
  {
    this.appConfigService = appConfigService;
    this.documentService = documentService;
    this.messageHandlerService = messageHandlerService;
    this.getOrganizations();
  }

  public get reasonForDeletion(): UntypedFormControl
  {
    return this.deleteForm.get('reasonForDeletion');
  }

  public ngOnInit(): void
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.view.setData(this.data);
  }

  public ngOnChanges(): void
  {
    this.loadData();
  }

  /**
   * Load documents to grid view.
   */
  public loadData(): void
  {
    this.data = [];
    if (!this.viewerOnly)
    {
      if (this.view !== undefined)
      {
        this.view.startLoading();
      }

      if (this.caseId !== undefined)
      {
        if (this.loadTasksAttachments)
        {
          this.loadDocuments('searchDocumentsByCaseAndTasks', this.caseId);
        }
        else
        {
          this.loadDocuments('searchDocumentsByCase', this.caseId);
        } 
      }
      else if (this.taskId !== undefined)
      {
        this.loadDocuments('searchDocumentsByTask', this.taskId);
        if (this.assessmentTaskId !== null && this.assessmentTaskId !== undefined)
        {
          this.loadDocuments('searchDocumentsByAssessmentTask', this.assessmentTaskId);
        }   
      }
      else if (this.assessmentTaskId !== null && this.assessmentTaskId !== undefined)
      {
        this.loadDocuments('searchDocumentsByAssessmentTask', this.assessmentTaskId);
      }      
      else if (this.messageId !== undefined)
      {
        this.loadDocuments('searchDocumentsByMessage', this.messageId);
      }
      else if (this.memberConsentId !== undefined)
      {
        this.loadDocuments('searchDocumentsByMemberConsent', this.memberConsentId);
      }
      else if (this.memberId !== undefined)
      {
        this.loadDocuments('searchDocumentsByMember', this.memberId);
      }
    }
  }

  public sendCaptureLink(): void
  {
    this.consentLinkMode = 'image-link';
    this.sendLinkDialogOpened = true;
  }

  public sendConsentLink(): void
  {
    this.consentLinkMode = 'consent-request';
    this.sendLinkDialogOpened = true;
  }

  public closingLinkDialog(): void
  {
    this.sendLinkDialogOpened = false;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * View document handler
   *
   * @param url Document Url
   * @param description Document description
   * @param mediaType Media mime type
   * @param createdOn Document created date
   */
  public viewHandler(url: string, description: string, mediaType: string, createdOn: string): void
  {
    const model = new DocumentViewerModel();
    model.url = url;
    model.description = description;
    model.mediaType = mediaType;
    model.createdOn = createdOn;

    this.documentViewer.execute(model);
  }

  /**
   * Save document to local
   *
   * @param url Document url
   * @param name Document file name
   */
  public saveFileLocal(url: string, name: string, isDisabled: boolean): void
  {
    if (isDisabled)
    {
      this.messageHandlerService.displayWarning('PHI-WARNING-EXPORT');
    }
    else
    {
      this.documentService.retrieveDocument(url).subscribe({
        next: (data) =>
        {
          if (data.isCDA)
          {
            this.messageHandlerService.displayWarning('PHI-WARNING-EXPORT');
          }
          else
          {
            saveAs(`data:${data.mimeType};base64,${data.content}`, name);
          }
        }
      });
    }
  }

  public setDocumentToDelete(url: string, name: string): void
  {
    this.documentToDeleteUrl = url;
    this.documentToDeleteName = name;
    // initialize the form
    this.deleteForm = new UntypedFormGroup(
      {
        reasonForDeletion: new UntypedFormControl(null)
      });
    this.deleteConfirmationOpened = true;
  }

  public cancelDelete(): void
  {
    this.documentToDeleteUrl = null;
    this.documentToDeleteName = null;
    this.deleteConfirmationOpened = false;
  }

  /**
   * Delete document selected
   */
  public deleteDocument(): void
  {
    if (this.documentToDeleteUrl !== null)
    {
      this.documentService.deleteDocument(this.documentToDeleteUrl, this.reasonForDeletion.value).subscribe({
        next: () =>
        {
          this.showTaskColumn = false;
          this.loadData();
          this.messageHandlerService.displaySuccess('DOCUMENT-DELETED-SUCCESSFULLY');
          this.deletedDocument.emit(true);
        },
        error: () =>
        {
          this.messageHandlerService.displayError('ERROR-DELETING-DOCUMENT');
        },
        complete: () =>
        {
          this.deleteConfirmationOpened = false;
        }
      });
    }
  }

  public onFileChanged(event: Event): void
  {
    const input = event.target as HTMLInputElement;
    const fileList: FileList = input.files;
    if (fileList.length > 0)
    {
      const file: File = fileList[0];
      const formData: FormData = new FormData();
      formData.append('file', file, file.name);
      formData.append('caseId', this.caseId !== undefined ? this.caseId : '');
      formData.append('memberId', this.memberId !== undefined ? this.memberId : '');
      formData.append('memberConsentId', this.memberConsentId !== undefined ? this.memberConsentId : '');
      formData.append('taskId', this.taskId !== undefined ? this.taskId : '');
      formData.append('assessmentTaskId', this.assessmentTaskId !== undefined ? this.assessmentTaskId : '');
      formData.append('taskDefinitionId', this.taskDefinitionId !== undefined ? this.taskDefinitionId : '');
      formData.append('bpmId', this.bpmId !== undefined ? this.bpmId : '');

      this.documentService.uploadDocument(formData).subscribe({
        next: () =>
        {
          // Show a success
          this.messageHandlerService.displaySuccess('DOCUMENT-ADDED-SUCCESFULLY');

          // And bind the new documents to show the recently added
          this.loadData();
          this.uploadedDocument.emit(true);
        },
        error: (error) =>
        {
          this.messageHandlerService.displayError(error);
        }
      });

      // Need to null out this value so if a "same named" file is attempted to upload, the control will fire the event
      // https://stackoverflow.com/questions/12030686/html-input-file-selection-event-not-firing-upon-selecting-the-same-file
      input.value = '';
    }
  }

  /**
   * Return the proper class to used based on the file mime type.
   *
   * @param mimeType Mime Type of the file.
   * @returns String
   */
  public getFileTypeClass(mimeType: string): string
  {
    return this.documentService.getFileTypeClass(mimeType);
  }

  public onSendDocument(url: string, name: string, isDisabled: boolean): void
  {
    if (isDisabled)
    {
      this.messageHandlerService.displayWarning('PHI-WARNING-EMAIL');
    }
    else
    {
      this.documentUrl = url;
      this.documentName = name;
      this.documentService.retrieveDocument(url).subscribe({
        next: (data) =>
        {
          // If CDA, prevent sending by email
          if (data.isCDA)
          {
            this.messageHandlerService.displayWarning('PHI-WARNING-EMAIL');
          }
          else
          {
            this.sendEmail.execute();
          }
        }
      });
    }
  }

  private loadDocuments(methodName: string, id: string): void
  {
    this.showDocumentCategory = false;
    this.showDocumentOrganization = false;

    this.documentService[methodName](id).subscribe({
      next: (foundDocument) =>
      {
        this.count = foundDocument.total;        
        foundDocument.data.forEach(document =>
        {
          // Show Document Category and Organization only if not from the Consent tab
          let organization = '';
          if (this.memberConsentId === undefined)
          {
            if (document.memberConsentOrganizationId !== null)
            {
              const foundOrg = this.organizations.find(x => x.id === document.memberConsentOrganizationId);
              if (foundOrg !== undefined)
              {
                organization = foundOrg.name;
                this.showDocumentOrganization = true;
              }
            }
            if (document.documentType !== null)
            {
              this.showDocumentCategory = true;
            }
          }

          this.data.push({
            url: document.url,
            description: document.name,
            mediaType: document.mimeType,
            date: new Date(document.createdOn),
            createdBy: document.createdBy,
            isDownloadDisabled: document.isDownloadDisabled,
            documentCategory: document.documentType !== null ? document.documentType.displayName : null,
            organizationName: organization,
            assessmentTaskId: document.assessmentTaskId,
            taskId: document.taskId,
            taskName: document.taskName
          });

          if ((document.taskId !== null) && (!this.showTaskColumn))
          {
            this.showTaskColumn = true;
          }
        });
        this.view.setData(this.data);
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }

  /**
   * Get all organizations
   */
  private getOrganizations(): void
  {
    this.organizationService.getCachedOrganizations().subscribe({
      next: (data) =>
      {
        this.organizations = data;
      },
      error: errorResponse =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }
}
